import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Search } from 'lucide-react';
import { adminApi } from '@/services/adminApi';
import type { AdminPaymentListItem, AdminPaymentsResponse } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { PAYMENT_STATUS_LABELS, type PaymentStatus } from '@printtat/shared';
import { PaymentMethodLabel, PageHeader, PageContent, EmptyState, LoadingState, Button, Table, Tabs } from '@printtat/ui';
import type { Column, SortState } from '@printtat/ui';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'paid', label: 'Paid' },
  { key: 'failed', label: 'Failed' },
];

const PAYMENT_BADGE_STYLES: Record<string, string> = {
  pending: 'bg-yellow-50 text-yellow-700',
  awaiting_payment: 'bg-amber-50 text-amber-700',
  paid: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  refunded: 'bg-gray-50 text-gray-700',
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = (cents: number, currency: string) => {
  const amount = (cents / 100).toLocaleString('en-EG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${amount} ${currency}`;
};

export const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', order: 'desc' });
  const limit = 20;

  const { data, isLoading } = useQuery<AdminPaymentsResponse>({
    queryKey: [...queryKeys.payments.all(), { status: statusFilter, search, page, limit, ...sort }],
    queryFn: () => adminApi.payments.list({
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: search || undefined,
      page,
      limit,
      sortBy: sort.key,
      sortOrder: sort.order,
    }),
  });

  const payments = data?.payments ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<AdminPaymentListItem>[] = [
    {
      key: 'order',
      header: 'Order',
      render: (p) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{p.orderNumber ?? '—'}</p>
          <p className="text-xs text-gray-500">{p.customerName}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (p) => (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_BADGE_STYLES[p.status] ?? 'bg-gray-100 text-gray-600'}`}>
          {PAYMENT_STATUS_LABELS[p.status as PaymentStatus] ?? p.status}
        </span>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      render: (p) => (
        <div className="text-sm text-gray-600">
          <PaymentMethodLabel
            paymentMethod={p.paymentMethod ? 'online' : 'cod'}
            paymentMethodDetail={p.paymentMethod}
            cardBrand={p.cardBrand}
            cardLastFour={p.cardLastFour}
            iconSize="w-3.5 h-3.5"
          />
        </div>
      ),
    },
    {
      key: 'amountCents',
      header: 'Amount',
      sortable: true,
      align: 'right',
      render: (p) => (
        <span className="text-sm font-medium text-gray-900">
          {formatAmount(p.amountCents, p.currency)}
        </span>
      ),
    },
    {
      key: 'transaction',
      header: 'Transaction ID',
      render: (p) => (
        <span className="text-xs font-mono text-gray-500">
          {p.paymobTransactionId ?? '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      render: (p) => (
        <span className="text-sm text-gray-500">{formatDate(p.createdAt)}</span>
      ),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        icon={CreditCard}
        title="Payments"
        description={`${total} total payment${total !== 1 ? 's' : ''}`}
      />

      <div className="mt-6 flex items-center justify-between gap-4">
        <Tabs
          tabs={STATUS_TABS}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          variant="pill"
        />

        <div className="relative min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order #, customer, or txn ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : payments.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title="No payments found"
            description={search || statusFilter !== 'all' ? 'Try adjusting your filters' : undefined}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={payments}
              keyExtractor={(p) => p.id}
              onRowClick={(p) => navigate(`/orders/${p.orderId}`)}
              sort={sort}
              onSortChange={(s) => { setSort(s); setPage(1); }}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  Previous
                </Button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PageContent>
  );
};
